import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Stack, Typography, Paper,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircleOutline as CheckCircleOutlineIcon } from '@mui/icons-material';

import { Container, LoadingState, ErrorState } from '@/components/common';
import { useQuestionDetail, useDeleteQuestion } from '@/hooks/aiReview.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { formatDate } from '@/util/dateUtils';
import { FileExplorer, ProjectCodeViewer, PromptTabsView, ApprovalRequestModal } from './components';

/**
 * 프로젝트 개발훈련 상세 페이지
 */
const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const languageId = searchParams.get('languageId');
  const [activeFileId, setActiveFileId] = useState(null);

  const { data: project, isLoading, error } = useQuestionDetail(projectId);
  const deleteQuestionMutation = useDeleteQuestion();
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);

  const files = project?.files ?? [];
  const practiceFile = useMemo(
    () => files.find((f) => !(f.readonly ?? f.isReadonly ?? false)),
    [files]
  );
  const prompts = project?.prompts ?? project?.prompt ?? [];
  const activeFile = useMemo(
    () => files.find((f) => f.id === activeFileId) ?? files[0],
    [files, activeFileId]
  );

  // 히스토리 뒤로가기(navigate(-1))는 편집→상세 순으로 오면 편집으로 돌아가므로 목록은 항상 명시 경로로 이동
  const goToProjectList = useCallback(() => {
    const query = languageId ? `?languageId=${languageId}` : '';
    navigate(`/education/projects${query}`);
  }, [navigate, languageId]);

  const handleDelete = useCallback(() => {
    confirmToast('이 프로젝트를 삭제하시겠습니까?', async () => {
      try {
        await deleteQuestionMutation.mutateAsync(projectId);
        successToast('프로젝트가 삭제되었습니다.');
        goToProjectList();
      } catch (err) {
        errorToast(`프로젝트 삭제에 실패했습니다. - ${err.response?.data?.msg}`);
      }
    });
  }, [projectId, deleteQuestionMutation, goToProjectList]);

  const handleEdit = useCallback(() => {
    const query = languageId ? `?languageId=${languageId}` : '';
    navigate(`/education/projects/edit/${projectId}${query}`);
  }, [navigate, projectId, languageId]);

  const handleApprove = useCallback(() => {
    setApprovalModalOpen(true);
  }, []);

  if (isLoading) {
    return <LoadingState title="프로젝트 로딩 중" />;
  }

  if (error || !project) {
    return (
      <ErrorState
        title="프로젝트 로딩 오류"
        message="프로젝트 정보를 불러오는 중 오류가 발생했습니다."
        onGoBack={goToProjectList}
      />
    );
  }

  const title = project.title ?? project.name ?? project.question ?? '-';
  const description = project.description ?? project.summary ?? '';
  const languageType = project.languageType ?? '';
  const createdAt = project.createdAt ?? project.regDate ?? '-';
  const modifiedAt = project.modifiedAt ?? '';

  return (
    <Container title="프로젝트 개발훈련 풀어보기">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={goToProjectList}
        >
          목록으로
        </Button>
        <Stack direction="row" gap={1}>
          {
            !project.isApproved && (
              <Button variant="outlined" startIcon={<CheckCircleOutlineIcon />} onClick={handleApprove}>
                승인 요청
              </Button>
            )
          }
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            수정
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleteQuestionMutation.isPending}
          >
            삭제
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
        <Stack direction="row" gap={2} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            등록일: {formatDate(createdAt)}
          </Typography>
          {modifiedAt && (
            <Typography variant="body2" color="text.secondary">
              수정일: {formatDate(modifiedAt)}
            </Typography>
          )}
          {languageType && (
            <Typography variant="body2" color="text.secondary">
              언어: {languageType}
            </Typography>
          )}
        </Stack>
        {description && (
          <Typography variant="body1" sx={{ mb: 0 }}>
            {description}
          </Typography>
        )}
      </Paper>

      <PromptTabsView prompts={prompts} />

      {files.length > 0 ? (
        <FileExplorer
          files={files}
          activeKey={activeFileId ?? activeFile?.id}
          selectionMode="id"
          mode="view"
          headerTitle="파일 탐색기"
          onSelectFile={setActiveFileId}
        >
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="body2" fontWeight={500}>
              {activeFile?.fileName ?? '-'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 1000 }}>
            <ProjectCodeViewer
              fileName={activeFile?.fileName ?? ''}
              content={activeFile?.fileContent ?? activeFile?.content ?? ''}
              readOnly
              showNotionViewer
            />
          </Box>
        </FileExplorer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            등록된 파일이 없습니다.
          </Typography>
        </Paper>
      )}

      <ApprovalRequestModal
        open={approvalModalOpen}
        onClose={() => setApprovalModalOpen(false)}
        projectId={projectId}
        file={practiceFile}
      />
    </Container>
  );
};

export default ProjectDetailPage;
